import * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as transformers from '@attain-apps/transformers'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import gql from 'graphql-tag'

import * as common from '../../../../common'
import type { Entity } from '../../types'

export const DOC = gql`
  ${common.gql.values.fragments.valueWithInfo.DOC}
  ${common.gql.faFunctions.fragments.categorizationIds.DOC}
  query Report(
    $tenantId: ID!
    $zoneId: ID!
    $appId: ID!
    $scenarioId: ID!
    $reportId: ID!
  ) {
    tenant(id: $tenantId) {
      zone(id: $zoneId) {
        app(id: $appId) {
          scenario(id: $scenarioId) {
            extra {
              rate {
                report(id: $reportId) {
                  content {
                    rows {
                      cells {
                        align
                        columnSpan
                        content {
                          ...FragmentValueWithInfo
                        }
                        indents
                        style
                      }
                    }
                    columns {
                      width
                    }
                  }
                  label {
                    code
                    faFunctionCategorization {
                      ...FragmentFaFunctionCategorizationIds
                    }
                    hint
                    id
                    isActive
                    name
                    position
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`

// NOTE: we use `nonNull` because if we don't receive a report, then the query
// has effectively failed
const TRANSFORMER = graphql.transformers.OUT.nonNull(
  graphql.transformers.OUT.select<
    graphql.schema.ReportQuery,
    Entity | undefined,
    'tenant'
  >(
    'tenant',
    graphql.transformers.OUT.select(
      'zone',
      graphql.transformers.OUT.select(
        'app',
        graphql.transformers.OUT.select(
          'scenario',
          graphql.transformers.OUT.select(
            'extra',
            graphql.transformers.OUT.select(
              'rate',
              graphql.transformers.OUT.select(
                'report',
                graphql.transformers.OUT.pipe(
                  graphql.transformers.OUT.object({
                    content: graphql.transformers.OUT.object({
                      // NOTE: we do not want to use `onNullish: 'remove'` because null values have
                      // special meanings
                      columns: graphql.transformers.OUT.list(
                        graphql.transformers.OUT.object({
                          width: transformers.PASS_THROUGH,
                        }),
                      ),
                      rows: graphql.transformers.OUT.default(
                        // NOTE: we do NOT want to remove nullish cells because they have a particular
                        // meaning
                        graphql.transformers.OUT.list(
                          graphql.transformers.OUT.object({
                            cells: graphql.transformers.OUT.default(
                              // NOTE: we do NOT want to remove nullish cells because they have a particular
                              // meaning
                              graphql.transformers.OUT.list(
                                graphql.transformers.OUT.object({
                                  align:
                                    common.gql.reports.enums.textAlignment
                                      .TRANSFORMER,
                                  columnSpan: transformers.PASS_THROUGH,
                                  content:
                                    common.gql.values.fragments.valueWithInfo
                                      .TRANSFORMER,
                                  indents: transformers.PASS_THROUGH,
                                  style:
                                    common.gql.reports.enums.cellStyle
                                      .TRANSFORMER,
                                }),
                              ),
                              [],
                            ),
                          }),
                        ),
                        [],
                      ),
                    }),
                    errorMessage: graphql.transformers.OUT.always(undefined),
                    label: graphql.transformers.OUT.object({
                      code: graphql.transformers.OUT.validate(
                        transformers.PASS_THROUGH,
                        TypeAsserter.assertIsNotNullish,
                        { warnOnly: true },
                      ),
                      faFunctionCategorization:
                        common.gql.faFunctions.fragments.categorizationIds
                          .TRANSFORMER,
                      hint: transformers.PASS_THROUGH,
                      id: graphql.transformers.OUT.validate(
                        transformers.PASS_THROUGH,
                        ents.extra.rate.Report.Id.assertIsId,
                        { warnOnly: true },
                      ),
                      isActive: graphql.transformers.OUT.validate(
                        transformers.PASS_THROUGH,
                        TypeAsserter.assertIsNotNullish,
                        { warnOnly: true },
                      ),
                      name: graphql.transformers.OUT.validate(
                        transformers.PASS_THROUGH,
                        TypeAsserter.assertIsNotNullish,
                        { warnOnly: true },
                      ),
                      position: graphql.transformers.OUT.validate(
                        transformers.PASS_THROUGH,
                        TypeAsserter.assertIsNotNullish,
                        { warnOnly: true },
                      ),
                    }),
                  }),
                  graphql.transformers.OUT.if(
                    TypeChecker.Factory.object({
                      label: TypeChecker.isNotNullish,
                    }),
                    transformers.PASS_THROUGH,
                    graphql.transformers.OUT.always(undefined),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  ),
)

export const executor = graphql.createExecutor<
  graphql.schema.ReportQuery,
  Entity,
  graphql.schema.ReportQueryVariables
>({
  query: DOC,
  transformer: TRANSFORMER,
})
